import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';

export interface AiAssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  error?: boolean;
  createdAt?: string;
}

export interface AiAssistantScreenContext {
  route?: string;
  patientId?: string | null;
  shiftId?: string | null;
}

export interface AiUsage {
  used: number;
  limit: number;
}

export interface AiConversationSummary {
  id: string;
  title: string | null;
  createdAt: string;
  preview: string | null;
}

const uid = () => Math.random().toString(36).slice(2);

const PAGE_SIZE = 20;
const CONVERSATIONS_PAGE_SIZE = 20;

function toMessages(rows: { id: string; role: string; content: string; created_at: string }[]) {
  return rows
    .filter((r) => r.role === 'user' || r.role === 'assistant')
    .map((r) => ({
      id: r.id,
      role: r.role as 'user' | 'assistant',
      content: r.content,
      createdAt: r.created_at,
    }));
}

export function useAiAssistant() {
  const { i18n, t } = useTranslation();
  const [messages, setMessages] = useState<AiAssistantMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [isRestoring, setIsRestoring] = useState(true);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [conversations, setConversations] = useState<AiConversationSummary[]>([]);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const restoredRef = useRef(false);

  const startNewConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setHasMoreMessages(false);
  }, []);

  // Carrega as últimas PAGE_SIZE mensagens de uma conversa.
  const openConversation = useCallback(async (id: string) => {
    const { data: rows } = await supabase
      .from('ai_messages')
      .select('id, role, content, created_at')
      .eq('conversation_id', id)
      .order('created_at', { ascending: false })
      .limit(PAGE_SIZE + 1);

    const list = rows ?? [];
    const hasMore = list.length > PAGE_SIZE;
    setConversationId(id);
    setHasMoreMessages(hasMore);
    setMessages(toMessages(list.slice(0, PAGE_SIZE).reverse()));
  }, []);

  const loadOlderMessages = useCallback(async () => {
    if (!conversationId || isLoadingOlder) return;
    const oldest = messages.find((m) => m.createdAt)?.createdAt;
    if (!oldest) return;

    setIsLoadingOlder(true);
    try {
      const { data: rows } = await supabase
        .from('ai_messages')
        .select('id, role, content, created_at')
        .eq('conversation_id', conversationId)
        .lt('created_at', oldest)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE + 1);

      const list = rows ?? [];
      setHasMoreMessages(list.length > PAGE_SIZE);
      const older = toMessages(list.slice(0, PAGE_SIZE).reverse());
      if (older.length) setMessages((prev) => [...older, ...prev]);
    } finally {
      setIsLoadingOlder(false);
    }
  }, [conversationId, isLoadingOlder, messages]);

  const loadConversations = useCallback(async (reset = true) => {
    setIsLoadingConversations(true);
    try {
      const offset = reset ? 0 : conversations.length;
      const { data: convs } = await supabase
        .from('ai_conversations')
        .select('id, title, created_at')
        .order('created_at', { ascending: false })
        .range(offset, offset + CONVERSATIONS_PAGE_SIZE);

      const list = convs ?? [];
      const hasMore = list.length > CONVERSATIONS_PAGE_SIZE;
      const page = list.slice(0, CONVERSATIONS_PAGE_SIZE);

      let previews: Record<string, string> = {};
      if (page.length) {
        const { data: msgs } = await supabase
          .from('ai_messages')
          .select('conversation_id, content, created_at, role')
          .in(
            'conversation_id',
            page.map((c) => c.id),
          )
          .eq('role', 'user')
          .order('created_at', { ascending: true });
        previews = (msgs ?? []).reduce<Record<string, string>>((acc, m) => {
          if (!acc[m.conversation_id]) acc[m.conversation_id] = m.content;
          return acc;
        }, {});
      }

      const mapped: AiConversationSummary[] = page.map((c) => ({
        id: c.id,
        title: c.title,
        createdAt: c.created_at,
        preview: c.title ?? previews[c.id] ?? null,
      }));

      setHasMoreConversations(hasMore);
      setConversations((prev) => (reset ? mapped : [...prev, ...mapped]));
    } finally {
      setIsLoadingConversations(false);
    }
  }, [conversations.length]);

  const deleteConversation = useCallback(
    async (id: string) => {
      const { error } = await supabase.from('ai_conversations').delete().eq('id', id);
      if (error) return false;
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (conversationId === id) startNewConversation();
      return true;
    },
    [conversationId, startNewConversation],
  );

  // Restaura a última conversa do usuário (persistida no backend) ao abrir o app.
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        if (!auth?.user) return;

        const { data: conv } = await supabase
          .from('ai_conversations')
          .select('id')
          .eq('user_id', auth.user.id)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (!conv?.id || cancelled) return;

        await openConversation(conv.id);
      } finally {
        if (!cancelled) setIsRestoring(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [openConversation]);

  const errorFor = useCallback(
    (code?: string) => {
      if (code === 'quota_exceeded') return t('aiAssistant.errors.quota');
      if (code === 'rate_limited') return t('aiAssistant.errors.rateLimited');
      if (code === 'ai_credits_exhausted') return t('aiAssistant.errors.credits');
      if (code === 'assistant_disabled') return t('aiAssistant.errors.disabled');
      return t('aiAssistant.errors.generic');
    },
    [t],
  );

  const sendMessage = useCallback(
    async (text: string, screen?: AiAssistantScreenContext) => {
      const content = text.trim();
      if (!content || isLoading) return;

      setMessages((prev) => [...prev, { id: uid(), role: 'user', content }]);
      setIsLoading(true);

      try {
        const { data, error } = await supabase.functions.invoke('ai-assistant', {
          body: {
            message: content,
            conversationId,
            language: i18n.language?.startsWith('en') ? 'en' : 'pt-BR',
            screen: screen ?? null,
          },
        });

        if (error) {
          let code: string | undefined;
          try {
            const parsed = JSON.parse((await (error as { context?: Response }).context?.text?.()) ?? '{}');
            code = parsed?.error;
          } catch {
            code = undefined;
          }
          setMessages((prev) => [
            ...prev,
            { id: uid(), role: 'assistant', content: errorFor(code), error: true },
          ]);
          return;
        }

        if (data?.error) {
          setMessages((prev) => [
            ...prev,
            { id: uid(), role: 'assistant', content: errorFor(data.error), error: true },
          ]);
          return;
        }

        if (data?.conversationId) setConversationId(data.conversationId);
        if (data?.usage) setUsage(data.usage);
        setMessages((prev) => [
          ...prev,
          { id: uid(), role: 'assistant', content: data?.answer ?? errorFor() },
        ]);
      } catch {
        setMessages((prev) => [
          ...prev,
          { id: uid(), role: 'assistant', content: errorFor(), error: true },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [conversationId, errorFor, i18n.language, isLoading],
  );

  return {
    messages,
    isLoading,
    isRestoring,
    usage,
    sendMessage,
    conversationId,
    hasMoreMessages,
    isLoadingOlder,
    loadOlderMessages,
    conversations,
    hasMoreConversations,
    isLoadingConversations,
    loadConversations,
    openConversation,
    startNewConversation,
    deleteConversation,
    reset: startNewConversation,
  };
}
